import type { AIModel } from '@volt/shared';
import { availableChatModels, modelRecencyScore } from './modelPicker.js';

/** Small/low-latency tiers. (The picker's difficulty filter ranks "GPT-4.1 mini" as complex, which is wrong for latency.) */
export function isQuickModel(model: AIModel): boolean {
  return /(^|[\s\-_.])(mini|nano|flash|haiku|lite|fast|instant)($|[\s\-_.])/i.test(`${model.displayName} ${model.modelCode}`);
}

/**
 * The chat model used for a voice call. A spoken reply is short, so a quick model
 * answers sooner and costs less; the composer's choice is kept when it already is
 * quick, when the user asked for it, or when no quick model is usable.
 */
export function pickVoiceModelId(
  models: AIModel[],
  chatModelId: string | undefined,
  preference: 'fast' | 'chat',
): string | undefined {
  if (preference === 'chat' || !chatModelId) return chatModelId;
  const chosen = models.find((model) => model.id === chatModelId);
  if (!chosen || isQuickModel(chosen)) return chatModelId;

  const quick = availableChatModels(models).filter(isQuickModel);
  if (quick.length === 0) return chatModelId;
  // Same vendor first (same account, familiar style), then the newest.
  const sameVendor = quick.filter((model) => model.providerCode === chosen.providerCode);
  const pool = sameVendor.length > 0 ? sameVendor : quick;
  return [...pool].sort((a, b) => modelRecencyScore(b) - modelRecencyScore(a))[0]!.id;
}
